import { log, LogCategory } from '../../../../../map-editor-shared/lib/logger'
import { FileStorageService } from '../../../../../services/FileStorageService'
import { DxfParserService } from '../../../../../services/DxfParserService'
import { useFloorStore } from '../../../../../map-editor-shared/store/floorStore'
import { useProjectStore } from '../../../../../map-editor-shared/store/projectStore'
import type { FileInfo } from '../../../../../models/FileStorage'
import { DwgCleanCancelled } from './useDwgCleanStep'
import type { ViewMode } from '../types'

interface UseLocalUploadDeps {
  setViewMode: (mode: ViewMode) => void
  setUploadState: (state: any) => void
  setFile: (file: File) => void
  parseFile: () => Promise<void>
  setDownloadError: (error: string | null) => void
  setDownloadMessage: (message: string) => void
  setSelectedDxfFile: (file: any) => void
  setAnalyzedLayers: (layers: any[]) => void
  setShowLayerModal: (show: boolean) => void
  onMappingRequired?: () => void
  resolveDwg: (file: FileInfo) => Promise<FileInfo>
  currentProjectId: number | null
  siteProjectId: number | null
  storeCurrentFloor: string | null
  currentFloor?: string | null
}

export function useLocalUpload(deps: UseLocalUploadDeps) {
  const {
    setViewMode, setUploadState, setFile, parseFile,
    setDownloadError, setDownloadMessage,
    setSelectedDxfFile, setAnalyzedLayers, setShowLayerModal,
    onMappingRequired, resolveDwg, currentProjectId, siteProjectId, storeCurrentFloor, currentFloor,
  } = deps

  const handleLocalFileSelect = async (file: File) => {
    try {
      log.info(LogCategory.CSV, 'Local CSV file selected', {
        fileName: file.name,
        size: file.size
      })

      setViewMode('parsing')
      setUploadState({
        status: 'uploading',
        progress: 100
      })

      // Set file and trigger parsing
      setFile(file)

      // Parse the file
      await parseFile()

      log.info(LogCategory.CSV, 'CSV file parsed successfully')
      setViewMode('parsed')

      // Trigger layer mapping modal after successful parsing
      if (onMappingRequired) {
        onMappingRequired()
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'CSV 파일 분석에 실패했습니다.'
      log.error(LogCategory.CSV, 'CSV parse failed', { error: errorMessage })

      setDownloadError(errorMessage)
      setViewMode('error')
      setUploadState({
        status: 'error',
        message: errorMessage
      })
    }
  }

  const handleLocalDXFFileSelect = async (file: File) => {
    if (!siteProjectId) {
      setDownloadError('사이트 프로젝트 ID가 없습니다.')
      return
    }

    // Auto-create B1 floor if no floor exists
    if (!storeCurrentFloor && !currentFloor) {
      const lotId = useProjectStore.getState().currentLot
      if (!lotId) {
        setDownloadError('프로젝트를 먼저 선택해주세요.')
        setViewMode('error')
        return
      }
      log.info(LogCategory.CSV, 'No floor exists, auto-creating default B1 floor', { lotId })
      useFloorStore.getState().addFloor(lotId)
    }

    try {
      const activeFloor = useFloorStore.getState().currentFloor || storeCurrentFloor || currentFloor
      log.info(LogCategory.CSV, 'Local DXF file selected', {
        fileName: file.name,
        size: file.size,
        siteProjectId,
        mapId: currentProjectId,
        currentFloor: activeFloor
      })

      setViewMode('downloading')
      setDownloadMessage('DXF 파일을 서버에 업로드하는 중...')
      setUploadState({
        status: 'uploading',
        progress: 10
      })

      // Step 1: Upload DXF file to server at flat path {projectId}/uploads/cad/.
      const uploadResult = await FileStorageService.uploadFiles(
        String(siteProjectId),
        'cad',
        [file],
        (progress) => {
          setUploadState({
            status: 'uploading',
            progress: Math.min(10 + progress * 0.3, 40) // 10% ~ 40%
          })
        }
      )

      if (!uploadResult.success || !uploadResult.data || uploadResult.data.uploaded_files.length === 0) {
        throw new Error('DXF 파일을 서버에 업로드하지 못했습니다.')
      }

      log.info(LogCategory.CSV, 'DXF file uploaded to server', { serverFilename: uploadResult.data.uploaded_files[0].filename })

      // DWG 면 맵에 필요 없는 레이어를 지운 DXF 로 바꾼다(남길 레이어는 사람이 확인).
      const uploadedFileInfo = await resolveDwg(uploadResult.data.uploaded_files[0])
      const serverFilename = uploadedFileInfo.filename

      setDownloadMessage('DXF 레이어 분석 중...')
      setUploadState({
        status: 'uploading',
        progress: 50
      })

      // Step 2: Analyze DXF layers from the flat {projectId}/uploads/cad/ path.
      const analysisResult = await DxfParserService.analyzeLayers(siteProjectId, serverFilename)

      if (!analysisResult.success || !analysisResult.data) {
        throw new Error(analysisResult.message || 'DXF 레이어 분석 실패')
      }

      log.info(LogCategory.CSV, 'DXF layer analysis complete', {
        totalLayers: analysisResult.data.totalLayers
      })

      // Step 3: Show layer selection modal
      // Use the uploaded file info directly from server response
      setSelectedDxfFile(uploadedFileInfo)
      setAnalyzedLayers(analysisResult.data.layers)
      setViewMode('source-select') // Keep source selector visible with modal overlay
      setUploadState({ status: 'idle' }) // Reset upload state to stop progress bar
      setShowLayerModal(true)

    } catch (error) {
      if (error instanceof DwgCleanCancelled) {
        // 올린 DWG 는 서버 CAD 목록에 남아 있다 — 거기서 다시 고를 수 있다.
        setUploadState({ status: 'idle' })
        setViewMode('server-cad')
        return
      }
      const errorMessage = error instanceof Error ? error.message : 'DXF 파일 업로드/분석에 실패했습니다.'
      log.error(LogCategory.CSV, 'Local DXF processing failed', { error: errorMessage })

      setDownloadError(errorMessage)
      setViewMode('error')
      setUploadState({
        status: 'error',
        message: errorMessage
      })
    }
  }

  return { handleLocalFileSelect, handleLocalDXFFileSelect }
}
